import React, { useState, useCallback } from 'react'
import { FormattedMessage } from 'react-intl'
import styled from 'styled-components'
import { getItineraryUniqueKey, getCruiseUniqueItineraryList } from 'lib/cruises/cruiseUtils'
import CruiseAccordion from './CruiseAccordion'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import CheckboxInput from 'LuxKit/Checkbox/CheckboxInput'
import Group from 'components/utils/Group'
import { arrayToObject } from 'lib/array/arrayUtils'
import ItinerarySideLine from './ItinerarySideLine'
import { CruiseType } from 'constants/cruise'

const TitleBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const ItineraryAccordionArea = styled.div`
  flex-grow: 1;
`

interface Props {
  itinerary: Array<App.CruiseItineraryItem>
  title?: string
  portNameSelected?: string | null
  itineraryKeySelected?: string | null
  cruiseType: App.CruiseType
  onSelectPort?: (port?: App.CruisePort, itineraryKey?: string) => void
}

export type UniqueItineraryItem = Record<string, {
  item: App.CruiseItineraryItem
  addPortCircle: boolean
  portName: string
  portNumber: number
  isStart: boolean
  isEnd: boolean
  itineraryKey: string
  isSelected: boolean
}>

function CruiseItinerary(props: Props) {
  const { itinerary, title, onSelectPort, portNameSelected, itineraryKeySelected, cruiseType } = props
  const [expandedAccordions, setExpandedAccordions] = useState<Record<string, boolean>>(arrayToObject(itinerary, item => `${item.startDay}`, () => false))
  const [showOnlyPorts, setShowOnlyPorts] = useState<boolean>(false)

  // Remove duplicate days at sea
  const uniqueItineraryList = getCruiseUniqueItineraryList(
    itinerary,
    showOnlyPorts,
    portNameSelected,
    itineraryKeySelected,
  )

  const toggleAccordion = useCallback((key: string, item: App.CruiseItineraryItem) => {
    const newExpandedValue = !expandedAccordions[key]
    setExpandedAccordions({ ...expandedAccordions, [key]: newExpandedValue })

    if (onSelectPort) {
      onSelectPort(item.port ?? undefined, key)
    }
  }, [expandedAccordions, onSelectPort])

  const toggleAllAccordion = useCallback(() => {
    const hasClosedItems = Object.values(expandedAccordions).some(item => !item)

    if (hasClosedItems) {
      setExpandedAccordions(arrayToObject(itinerary, item => getItineraryUniqueKey(item), () => true))
    } else {
      setExpandedAccordions(arrayToObject(itinerary, item => getItineraryUniqueKey(item), () => false))
    }
  }, [expandedAccordions, itinerary])

  return <Group direction="vertical" gap={20}>
    <Group direction="vertical" gap={4}>
      {!!title && <TitleBar>
        <Heading variant="heading3" align="center">{title}</Heading>
      </TitleBar>}

      <BodyText variant="medium">
        <FormattedMessage
          id="YVvmvV7SoHQvseVA0GuX"
          defaultMessage="Please note that all dates and times are displayed in local time."
          description="Note about dates and times being in local time in the cruises section"
        />
      </BodyText>
    </Group>

    {cruiseType === CruiseType.OCEAN && <Group direction="horizontal" gap={20}>
      <CheckboxInput
        size="large"
        onChange={() => setShowOnlyPorts(!showOnlyPorts)}
        checked={showOnlyPorts}
      >
        <FormattedMessage
          id="rQ6uTjk-ekflMnkg6pou"
          defaultMessage="Show ports only"
          description="Checkbox label to filter itinerary to show only ports"
        />
      </CheckboxInput>

      <CheckboxInput
        size="large"
        onChange={toggleAllAccordion}
        checked={Object.values(expandedAccordions).every(item => item)}
      >
        <FormattedMessage
          id="CcTldz1NZENfowa94J5V"
          defaultMessage="Expand all"
          description="Checkbox label to expand all itinerary items in the itinerary"
        />
      </CheckboxInput>
    </Group>}

    <div>
      {uniqueItineraryList.map(({ item, portNumber, portName, isStart, isEnd, addPortCircle, itineraryKey, isSelected }) => {
        return <Group
          key={itineraryKey}
          direction="horizontal"
          gap={16}
        >
          <ItinerarySideLine
            portName={portName}
            portNumber={portNumber}
            isStart={isStart}
            isEnd={isEnd}
            addPortCircle={addPortCircle}
          />
          <ItineraryAccordionArea>
            <CruiseAccordion
              itineraryItem={item}
              isSelected={isSelected}
              cruiseType={cruiseType}
              open={expandedAccordions[itineraryKey] && !!item.description}
              toggleAccordion={toggleAccordion}
            />
          </ItineraryAccordionArea>
        </Group>
      })}
    </div>
  </Group>
}

export default CruiseItinerary
